import React from 'react';
import { Pagination } from '@cp/ds/src/components/pagination';
import { PaginationProps } from '@cp/ds/src/components/pagination/model';
import { getLastPageFromTotalCount } from '@common/utils/page';
import { DEFAULT_CATALOG_PAGE_NUMBER, DEFAULT_CATALOG_PER_PAGE } from '@common/const/catalog';
import { ReactAppPageNotFoundError } from '@common/model/reactApp/error';
import { coreCatalogApi } from '@cp/entities/catalog';
import { SxProps } from '@cp/ds';
import { CatalogItemListRequest } from '@common/model/catalog/item/list.request';
import { useHelmet } from 'src/shared/helmet';

export interface CatalogPaginationProps {
  listRequest: CatalogItemListRequest;
  onChangePage: (nextPage: number) => void;
  pageLinkGenerator: (page: number) => string;
  sx?: SxProps;
}

export const CatalogPagination: React.FC<CatalogPaginationProps> = ({ sx, listRequest, onChangePage, pageLinkGenerator }) => {
  const Helmet = useHelmet();
  const { data } = coreCatalogApi.useGetItemsQuery(listRequest);
  const page = listRequest.page ?? DEFAULT_CATALOG_PAGE_NUMBER;
  const lastPage = getLastPageFromTotalCount(data?.totalCount, DEFAULT_CATALOG_PER_PAGE);

  if (!Number.isInteger(page) || (data?.totalCount && page > lastPage)) {
    throw new ReactAppPageNotFoundError();
  }

  const handleChange = React.useCallback(
    (event, nextPage: number) => {
      event.preventDefault();
      onChangePage(nextPage);
    },
    [onChangePage],
  );

  if (!data?.totalCount) {
    return null;
  }

  const renderedMetaTags: React.ReactNode[] = [];

  if (page > 1) {
    renderedMetaTags.push(<link href={pageLinkGenerator(page - 1)} key={`prev-${page - 1}`} rel="prev" />);
  }

  if (page < lastPage) {
    renderedMetaTags.push(<link href={pageLinkGenerator(page + 1)} key={`next-${page + 1}`} rel="next" />);
  }

  const paginationProps: PaginationProps = {
    currentPage: page,
    lastPage,
    pageLinkGenerator,
    onChange: handleChange,
    sx,
  };

  return (
    <>
      <Helmet>{renderedMetaTags}</Helmet>
      <Pagination {...paginationProps} />
    </>
  );
};
